import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, AlertDescription, Button, Textarea } from '@/components/ui'
import { refusalCode, sendNew } from '@/services/helpFeedback/appFeedbackApi'
import { useHelpFeedbackStore } from '@/stores/helpFeedbackStore'
import { turnBody, type Conversation } from './conversation'
import { AttachedRow } from './AttachedRow'
import { KindChips } from './KindChips'
import { Receipt } from './Receipt'
import {
  PLAIN_HELP_LEAD, PLAIN_HELP_LINK, PLAIN_LABEL, PLAIN_LEAD, PLAIN_PLACEHOLDER, SEND, TRY_AGAIN,
  refusalReason, sendFailedLine, type CardKind,
} from './helpFeedbackText'

/**
 * When the assistant is not set up (decision 18): a plain form that goes straight to the team, with
 * the same attached chips and receipt.
 */
export function PlainReport({ c, viewAs }: { c: Conversation; viewAs: boolean }) {
  const id = useId()
  const dispatch = useHelpFeedbackStore((s) => s.dispatch)
  const [kind, setKind] = useState<CardKind>('problem')
  const [words, setWords] = useState(() => useHelpFeedbackStore.getState().plainWords)
  const [error, setError] = useState<string | null>(null)
  const sending = c.waiting === 'send'

  if (c.sent) return <Receipt reference={c.sent.reference} />

  const send = async () => {
    const text = words.trim()
    if (!text) return
    const base = turnBody(c, text, false)
    setError(null)
    dispatch({ type: 'sending' })
    try {
      const report = await sendNew({
        kind,
        summary: text.slice(0, 2000),
        attempting: null,
        page: base.page,
        context: base.context,
        removed: [...c.removed],
        resolved_by_guidance: false,
        message: text,
        spoken: false,
      })
      useHelpFeedbackStore.getState().setPlainWords('')
      dispatch({ type: 'sent', report })
    } catch (err) {
      const reason = refusalReason(refusalCode(err))
      setError(reason)
      dispatch({ type: 'sendFailed', reason })
    }
  }

  return (
    <div className="space-y-3 text-sm">
      <p>{PLAIN_LEAD}</p>
      <KindChips value={kind} onChange={setKind} disabled={sending} />
      <div className="space-y-1">
        <label htmlFor={id} className="text-xs font-medium text-muted-foreground">{PLAIN_LABEL}</label>
        <Textarea
          id={id}
          rows={5}
          value={words}
          placeholder={PLAIN_PLACEHOLDER}
          onChange={(e) => {
            setWords(e.target.value)
            useHelpFeedbackStore.getState().setPlainWords(e.target.value)
          }}
          className="text-sm"
        />
      </div>
      <AttachedRow snapshot={c.snapshot} removed={c.removed} onRemove={(chip) => dispatch({ type: 'removeChip', chip })} />
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{sendFailedLine(error)}</AlertDescription>
        </Alert>
      )}
      <Button size="sm" onClick={() => void send()} disabled={viewAs || !words.trim()} isLoading={sending}>
        {error ? TRY_AGAIN : SEND}
      </Button>
      <p className="text-xs text-muted-foreground">
        {PLAIN_HELP_LEAD}{' '}
        <Link to="/help" className="text-primary underline-offset-4 hover:underline">{PLAIN_HELP_LINK}</Link>
      </p>
    </div>
  )
}
